/**
 * API client for the Zone Ambient SDK demo backend
 */

const API_BASE = '/api'

export interface ConfigPayload {
  policy_text: string
  cooldown_minutes: number
  repetition_threshold: number
  repetition_window_minutes: number
  max_alerts_per_hour: number
  urgency_threshold: 'low' | 'medium' | 'high'
  tools_enabled: {
    email: boolean
    whatsapp: boolean
    ticket: boolean
    digest: boolean
  }
  tool_params: {
    to_email?: string
    to_phone?: string
    ticket_project?: string
  }
  model: string
  debug: boolean
  dry_run: boolean
}

export interface EventPayload {
  event_type: string
  text: string
  attributes?: Record<string, unknown>
}

export interface EngineResult {
  event: {
    event_id: string
    ts: string
    source: string
    event_type: string
    text: string
    attributes: Record<string, unknown>
  }
  decision: {
    decision_type: 'IGNORE' | 'DIGEST' | 'ACT' | 'SUPPRESSED'
    why: string
    incident_key: string | null
    urgency: 'low' | 'medium' | 'high'
    tool_calls: Array<{
      name: string
      arguments: Record<string, unknown>
    }>
    suppressed_reason: string | null
    debug: Record<string, unknown> | null
  }
  executions: Array<{
    tool_name: string
    ok: boolean
    status: 'success' | 'fail' | 'skip'
    message: string
    payload_preview: Record<string, unknown> | null
    external_id: string | null
    ts: string
  }>
  audit_id: string
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }
  return response.json()
}

export async function getConfig(): Promise<ConfigPayload> {
  const response = await fetch(`${API_BASE}/config`)
  return handleResponse(response)
}

export async function saveConfig(config: ConfigPayload): Promise<{ ok: boolean }> {
  const response = await fetch(`${API_BASE}/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  })
  return handleResponse(response)
}

export async function sendEvent(event: EventPayload): Promise<EngineResult> {
  const response = await fetch(`${API_BASE}/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
  })
  return handleResponse(response)
}

export async function testEmail(to_email: string): Promise<{ ok: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/test/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to_email }),
  })
  return handleResponse(response)
}

export async function testWhatsApp(to_phone: string): Promise<{ ok: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/test/whatsapp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to_phone }),
  })
  return handleResponse(response)
}

export async function getLogs(): Promise<{
  events: unknown[]
  decisions: unknown[]
  executions: unknown[]
  digest: unknown[]
}> {
  const response = await fetch(`${API_BASE}/logs`)
  return handleResponse(response)
}

export interface StateStats {
  events: number
  executions: number
  cooldowns: number
  digest: number
  audit_log: number
}

export async function getStateStats(): Promise<StateStats> {
  const response = await fetch(`${API_BASE}/state/stats`)
  return handleResponse(response)
}

export async function resetState(): Promise<{ ok: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/state/reset`, {
    method: 'POST',
  })
  return handleResponse(response)
}
